// src/utils/secureRoutes.js

import { useNavigate } from "react-router-dom";

/*
|--------------------------------------------------------------------------
| Configuration
|--------------------------------------------------------------------------
|
| The real route is carried in a single query param on an otherwise
| ordinary-looking URL, surrounded by decoy params modeled on the kind of
| tracking noise big e-commerce sites (Amazon, etc.) attach to every link:
| qid, ref, pd_rd_r, pd_rd_w, pd_rd_wg, pf_rd_p, pf_rd_r, _encoding.
|
| Example output at the default "light" decoy level:
|
|   /prestigehome?ref=k3f8q1&qid=246955&rh=8f3Kx91Lm...
|
| Pass { decoyLevel: "full" } for the longer Amazon-style param set, or
| { decoyLevel: "off" } for just "/prestigehome?rh=8f3Kx91Lm...".
|
| None of this is real security — see the note at the bottom of this file.
| It just means a glance at the URL bar or a shared link doesn't read as
| "here is an internal route", the way /s/<base64> obviously does.
|
|--------------------------------------------------------------------------
*/

const readEnv = (key) => {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env) {
      return import.meta.env[key];
    }
  } catch (error) {
    // import.meta not available in this build target, fall through
  }

  if (typeof process !== "undefined" && process.env) {
    return process.env[key];
  }

  return undefined;
};

const DEFAULTS = {
  basePath: readEnv("VITE_SECURE_ROUTE_BASE_PATH") || "/prestigehome",
  paramKey: readEnv("VITE_SECURE_ROUTE_PARAM") || "rh",
  secret: readEnv("VITE_SECURE_ROUTE_SECRET") || "CogniVisionRoute2026SecureKey",
  // "off" | "light" | "full" — see buildDecoyParams below
  decoyLevel: readEnv("VITE_SECURE_ROUTE_DECOY_LEVEL") || "light",
};

let config = { ...DEFAULTS };

/*
|--------------------------------------------------------------------------
| Configure / reset
|--------------------------------------------------------------------------
*/

export const configureSecureRoutes = (options = {}) => {
  config = { ...config, ...options };
};

export const resetSecureRoutesConfig = () => {
  config = { ...DEFAULTS };
};

export const getSecureRoutesConfig = () => ({ ...config });

/*
|--------------------------------------------------------------------------
| Convert string <-> bytes
|--------------------------------------------------------------------------
*/

const stringToBytes = (value) => new TextEncoder().encode(value);
const bytesToString = (bytes) => new TextDecoder().decode(bytes);

/*
|--------------------------------------------------------------------------
| Simple deterministic XOR transformation
|--------------------------------------------------------------------------
|
| This is URL obfuscation, NOT encryption/security. It is deterministic,
| reversible, synchronous, and dependency-free.
|
|--------------------------------------------------------------------------
*/

const xorTransform = (value, secret) => {
  const input = stringToBytes(value);
  const secretBytes = stringToBytes(secret);

  const output = new Uint8Array(input.length);

  for (let i = 0; i < input.length; i++) {
    output[i] = input[i] ^ secretBytes[i % secretBytes.length];
  }

  return output;
};

/*
|--------------------------------------------------------------------------
| Bytes <-> URL safe Base64
|--------------------------------------------------------------------------
*/

const bytesToBase64Url = (bytes) => {
  let binary = "";

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const base64UrlToBytes = (value) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

/*
|--------------------------------------------------------------------------
| Normalize a route to always start with "/"
|--------------------------------------------------------------------------
*/

const normalizeRoute = (route) => (route.startsWith("/") ? route : `/${route}`);

/*
|--------------------------------------------------------------------------
| Encode / decode the route value itself
|--------------------------------------------------------------------------
*/

export const encodeRoute = (route, options = {}) => {
  if (!route) {
    return "";
  }

  const secret = options.secret ?? config.secret;
  const normalizedRoute = normalizeRoute(route);

  return bytesToBase64Url(xorTransform(normalizedRoute, secret));
};

export const decodeRoute = (encodedRoute, options = {}) => {
  if (!encodedRoute) {
    return "/";
  }

  const secret = options.secret ?? config.secret;

  try {
    const bytes = base64UrlToBytes(encodedRoute);
    const secretBytes = stringToBytes(secret);
    const decoded = new Uint8Array(bytes.length);

    for (let i = 0; i < bytes.length; i++) {
      decoded[i] = bytes[i] ^ secretBytes[i % secretBytes.length];
    }

    return bytesToString(decoded);
  } catch (error) {
    console.error("Failed to decode route:", error);
    return "/";
  }
};

/*
|--------------------------------------------------------------------------
| Decoy query params
|--------------------------------------------------------------------------
|
| Purely cosmetic — modeled on the noise real e-commerce sites attach to
| links. They carry no information and are ignored on decode.
|
|--------------------------------------------------------------------------
*/

const ALNUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const randomAlnum = (length) => {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += ALNUM[Math.floor(Math.random() * ALNUM.length)];
  }
  return out;
};

const randomUuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const buildDecoyParams = (level) => {
  if (level === "off") {
    return {};
  }

  if (level === "full") {
    return {
      qid: `${Date.now()}`.slice(0, 10),
      ref: `pd_hp_d_r_atf_${randomAlnum(3).toLowerCase()}`,
      pd_rd_r: randomUuid(),
      pd_rd_w: randomAlnum(5),
      pd_rd_wg: randomAlnum(5),
      pf_rd_p: randomUuid(),
      pf_rd_r: randomAlnum(20).toUpperCase(),
      _encoding: "UTF8",
    };
  }

  // "light" (default) — just enough noise to not look like a bare token,
  // without dragging the URL out.
  return {
    ref: randomAlnum(6).toLowerCase(),
    qid: `${Date.now()}`.slice(-6),
  };
};

/*
|--------------------------------------------------------------------------
| Create browser URL
|--------------------------------------------------------------------------
|
| securePath("/prestigehome/reports") ->
|   "/prestigehome?rh=<token>&qid=...&ref=...&pd_rd_r=...&..."
|
| Pass { decoys: false } to skip the noise params, or { basePath, paramKey,
| secret } to override the configured defaults for a single call.
|
|--------------------------------------------------------------------------
*/

export const securePath = (route, options = {}) => {
  const normalizedRoute = normalizeRoute(route);
  const opts = typeof options === "object" && options !== null ? options : {};

  const basePath = opts.basePath ?? inferBasePath(normalizedRoute);
  const paramKey = opts.paramKey ?? config.paramKey;

  const decoyLevel =
    opts.decoyLevel ??
    (opts.decoys === undefined ? config.decoyLevel : opts.decoys ? "full" : "off");

  const params = new URLSearchParams();

  Object.entries(buildDecoyParams(decoyLevel)).forEach(([key, value]) => {
    params.set(key, value);
  });

  params.set(paramKey, encodeRoute(normalizedRoute, opts));

  return `${basePath}?${params.toString()}`;
};

/*
|--------------------------------------------------------------------------
| Extract and decode the route from a browser URL
|--------------------------------------------------------------------------
|
| Needs both pathname and search, since the token now lives in the query
| string rather than the path:
|
|   decodeSecurePath(location.pathname, location.search)
|
|--------------------------------------------------------------------------
*/

export const decodeSecurePath = (pathname, search = "", options = {}) => {
  const basePath = options.basePath ?? inferBasePath(pathname);
  const paramKey = options.paramKey ?? config.paramKey;

  if (pathname !== basePath && !pathname.startsWith(`${basePath}/`)) {
    return null;
  }

  const encoded = new URLSearchParams(search).get(paramKey);

  if (!encoded) {
    return "/";
  }

  return decodeRoute(encoded, options);
};

/*
|--------------------------------------------------------------------------
| Navigate helper
|--------------------------------------------------------------------------
*/

export const getSecureRoute = securePath;

/*
|--------------------------------------------------------------------------
| useSecureNavigate
|--------------------------------------------------------------------------
|
| Wraps react-router's useNavigate() so programmatic navigation elsewhere
| in the app goes through securePath() automatically, instead of every
| call site needing to remember to build the query string by hand:
|
|   const secureNavigate = useSecureNavigate();
|   secureNavigate(`${BASE_PATH}/reports`);
|   secureNavigate(`${BASE_PATH}/alerts`, { replace: true });
|
|--------------------------------------------------------------------------
*/

export const useSecureNavigate = () => {
  const navigate = useNavigate();

  return (route, navigateOptions = {}, secureOptions = {}) => {
    const target = securePath(route, secureOptions);
    const [pathname, search] = target.split("?");

    navigate({ pathname, search: `?${search}` }, navigateOptions);
  };
};

// "/empirehome/alerts" -> "/empirehome"
const inferBasePath = (route) => `/${normalizeRoute(route).split("/")[1]}`;

/*
|--------------------------------------------------------------------------
| A note on what this actually protects against
|--------------------------------------------------------------------------
|
| This still isn't encryption or access control — it's the same XOR-and-
| base64 obfuscation as before, just carried in a query param dressed up
| with decoy tracking params instead of an obvious /s/<token> path. Anyone
| who opens devtools, views the bundled JS, or just decodes the base64
| by hand can recover the route and the secret. If any of these routes
| need to actually be protected, that has to happen server-side / in an
| auth guard, not in this file.
|
|--------------------------------------------------------------------------
*/